import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import { JsonLd } from 'react-schemaorg';
import { getSrc } from 'gatsby-plugin-image';

import {
  removeLanguageFromPath,
  generateLanguagePath,
  getLanguageSiteUrl
} from '../utilities/url';
import { objectDeepReplace } from '../utilities/misc';
import {
  CN_SITE_URL,
  DEFAULT_LANGUAGE,
  JSON_LD_ORGANIZATION,
  LANGUAGES,
  SITE_URL
} from '../constants';

const Seo = ({
  language,
  title,
  description,
  image,
  jsonLd,
  noIndex = false,
  noFollow = false,
  canonicalPath = null,
  updatedAt = null
}) => {
  const { site } = useStaticQuery(graphql`
    query SEO {
      site {
        siteMetadata {
          title
        }
      }
    }
  `);
  const { pathname } = useLocation();
  const pathWithoutLanguage = removeLanguageFromPath(pathname);
  const cleanedPath = pathWithoutLanguage === '/' ? '' : pathWithoutLanguage;
  const baseUrl = SITE_URL;
  const languageUrl = `${getLanguageSiteUrl(language)}${generateLanguagePath(language)}`;
  const canonicalUrl = `${languageUrl}${canonicalPath || cleanedPath}`;
  const defaultLangPath = LANGUAGES[DEFAULT_LANGUAGE].path;
  const currentHrefLang = LANGUAGES[language].hreflang;
  const ogType = cleanedPath === '' ? 'website' : 'article';
  const robots = ['max-snippet:-1', 'max-image-preview:large', 'max-video-preview:-1'];
  const jsonLdOrgData = objectDeepReplace(JSON_LD_ORGANIZATION, /{{siteurl}}/g, getLanguageSiteUrl(language));
  let jsonLdPageData = null;

  if (noIndex) {
    robots.push('noindex');
  } else {
    robots.push('index');
  }

  if (noFollow) {
    robots.push('nofollow');
  } else {
    robots.push('follow');
  }

  if (jsonLd?.internal?.content !== undefined) {
    const parsedJsonLd = JSON.parse(jsonLd.internal.content);
    jsonLdPageData = objectDeepReplace(parsedJsonLd, /{{siteurl}}/g, languageUrl);
  }

  // Add https: if it's missing in the image URL
  let ogImageURL = getSrc(image) || '';
  if (ogImageURL.includes('//') && !ogImageURL.includes('http')) {
    ogImageURL = `https:${ogImageURL}`;
  }
  return (
    <>
      <JsonLd item={jsonLdOrgData} />
      {jsonLdPageData && (
        <JsonLd item={jsonLdPageData} />
      )}
      <title>{title || site.siteMetadata.title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="og:site_name" content="Splashtop Inc." />
      <meta name="article:publisher" content="https://www.facebook.com/Splashtop" />
      <meta name="og:locale" content={currentHrefLang} />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={description} />
      <meta name="og:url" content={canonicalUrl} />
      <meta name="og:type" content={ogType} />
      {updatedAt && (
        <meta name="article:modified_time" content={updatedAt} />
      )}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${baseUrl}${defaultLangPath ? `/${defaultLangPath}` : ''}${cleanedPath}`}
      />
      {Object.keys(LANGUAGES).map((lang) => {
        if (lang === 'jp' || lang === 'kr' || lang === 'th' || lang === 'id') return null;

        const hrefLang = LANGUAGES[lang].hreflang;
        let langUrl;

        if (lang === 'zh-Hans') {
          langUrl = `${CN_SITE_URL}${generateLanguagePath(lang, cleanedPath)}`;
        } else {
          langUrl = `${baseUrl}${generateLanguagePath(lang, cleanedPath)}`;
        }

        return (
          <link
            key={LANGUAGES[lang].name}
            rel="alternate"
            hrefLang={hrefLang}
            href={langUrl}
          />
        );
      })}
      {image && (
        <>
          <meta property="og:image" content={ogImageURL} />
          <meta property="og:image:width" content={image.gatsbyImageData.width} />
          <meta property="og:image:height" content={image.gatsbyImageData.height} />
          <meta property="og:image:type" content={image.file.contentType} />
          {image.description && (
            <meta name="og:image:alt" content={image.description} />
          )}
        </>
      )}
    </>
  );
};

Seo.propTypes = {
  language: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object,
    description: PropTypes.string,
    file: PropTypes.shape({
      contentType: PropTypes.string.isRequired,
    })
  }),
  // eslint-disable-next-line react/forbid-prop-types
  jsonLd: PropTypes.object,
  noIndex: PropTypes.bool,
  noFollow: PropTypes.bool,
  canonicalPath: PropTypes.string,
  updatedAt: PropTypes.string
};

export const query = graphql`
  fragment SeoFragment on ContentfulSeo {
    title
    description
    image {
      gatsbyImageData(width: 1200, height: 630)
      description
      file {
        contentType
      }
    }
    jsonLd {
      internal {
        content
      }
    }
    noIndex: no_index
    noFollow: no_follow
  }
  fragment ListingPageSeoFragment on ContentfulListingPageSeo {
    title
    description
    image {
      gatsbyImageData(width: 1200, height: 630)
      description
      file {
        contentType
      }
    }
    jsonLd {
      internal {
        content
      }
    }
    noIndex: no_index
    noFollow: no_follow
  }
`;

export default Seo;
